import React, { useEffect, useMemo, useState } from "react";
import { useVerifyEmail } from "../../hooks/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  alpha,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Fade,
  Grid,
  Stack,
  Typography,
  useTheme,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import {
  CheckCircleOutline,
  ErrorOutline,
  HourglassBottom,
  LoginOutlined,
  MarkEmailRead,
  ShieldOutlined,
} from "@mui/icons-material";
import SurfaceCard from "../../components/common/ui/SurfaceCard";
import { ApiError } from "../../error/errorTypes";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";
import BrandIcon from "../../components/common/BrandIcon";

const VerifyEmailConfirmPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const theme = useTheme();
  const navigate = useNavigate();
  const [success, setSuccess] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [redirectCountdown, setRedirectCountdown] = useState<number>(3);

  const { mutate: verifyEmail, isPending: isLoading } = useVerifyEmail();

  useEffect(() => {
   
    if (!token) {
      setError("Missing verification token.");
      setSuccess(false);
      return;
    }
    
    verifyEmail(token, {
      onSuccess: () => {
        setSuccess(true);
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          setError(err.userMessage || err.message);
        } else {
          setError("Email verification failed. The link may be invalid or expired.");
        }
        setSuccess(false);
      }
    });
  }, [token, verifyEmail]);

  useEffect(() => {
    if (!success) return;
    const timer = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(LOGIN_ROUTE_PATH);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [success, navigate]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `radial-gradient(circle at 18% 16%, ${alpha(theme.palette.primary.main, 0.16)} 0%, transparent 38%), ${theme.palette.background.default}`,
        display: "flex",
        alignItems: "center",
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="lg">
        <Fade in={true} timeout={700}>
          <SurfaceCard
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
              background: alpha(theme.palette.background.paper, 0.9),
              backdropFilter: "blur(8px)",
              boxShadow: "0 14px 44px rgba(2, 6, 23, 0.55)",
            }}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  p: { xs: 2.6, sm: 3.2, md: 3.4 },
                  borderRight: { md: `1px solid ${alpha(theme.palette.text.primary, 0.08)}` },
                  borderBottom: { xs: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`, md: "none" },
                  background: `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.background.paper, 0.35)})`,
                }}
              >
                <Box sx={{ mb: 2.2 }}>
                  <BrandIcon size={48} />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                  }}
                >
                  Confirm your email
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8, maxWidth: 360 }}>
                  We are validating your verification link so you can securely access your account.
                </Typography>

                <Stack spacing={1.05} sx={{ mt: 2.2 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 24, height: 24, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.light }}>
                      <ShieldOutlined fontSize="small" />
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">We are securely verifying your email link</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 24, height: 24, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.light }}>
                      <MarkEmailRead fontSize="small" />
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">Confirm your email to finish setting up your account</Typography>
                  </Stack>
                </Stack>

                <Chip
                  label="Verification in progress"
                  sx={{
                    mt: 2.2,
                    borderRadius: "10px",
                    color: theme.palette.primary.light,
                    backgroundColor: alpha(theme.palette.primary.main, 0.16),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={7} sx={{ p: { xs: 2.6, sm: 3.2, md: 3.8 } }}>
                {isLoading && (
                  <Box sx={{ textAlign: "center", py: { xs: 1.5, md: 2.5 } }}>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.8 }} role="status" aria-live="polite">
                      Verifying your email
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This usually takes only a moment.
                    </Typography>
                    <Chip
                      icon={<HourglassBottom />}
                      label="Verifying your link"
                      sx={{ mt: 1.6, borderRadius: "8px" }}
                    />
                  </Box>
                )}

                {!isLoading && success && (
                  <Box sx={{ textAlign: "center" }}>
                    <CheckCircleOutline sx={{ color: theme.palette.success.main, fontSize: 50, mb: 1.2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.7 }}>
                      You are all set
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1.6 }}>
                      Your email has been verified successfully. You can now sign in.
                    </Typography>

                    <Button
                      variant="contained"
                      onClick={() => navigate(LOGIN_ROUTE_PATH)}
                      startIcon={<LoginOutlined />}
                      sx={{
                        py: 1.15,
                        px: 2.4,
                        borderRadius: theme.radii?.medium ?? 10,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      }}
                    >
                      Go to sign in {redirectCountdown > 0 ? `(${redirectCountdown}s)` : ""}
                    </Button>
                  </Box>
                )}

                {!isLoading && success === false && (
                  <Box>
                    <Box sx={{ textAlign: "center", mb: 1.8 }}>
                      <ErrorOutline sx={{ color: theme.palette.error.main, fontSize: 50, mb: 1.2 }} />
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.7 }}>
                        Email verification failed
                      </Typography>
                    </Box>

                    {error && (
                      <Alert severity="error" sx={{ mb: 1.8, borderRadius: 2 }}>
                        {error}
                      </Alert>
                    )}

                    <Divider sx={{ mb: 1.8 }} />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1} justifyContent="center">
                      <Button variant="contained" onClick={() => navigate(LOGIN_ROUTE_PATH)}>
                        Back to sign in
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Grid>
            </Grid>
          </SurfaceCard>
        </Fade>
      </Container>
    </Box>
  );
};

export default VerifyEmailConfirmPage;


